import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  MapPin, Mail, Phone, Send, MessageSquare, User, 
  HelpCircle, Clock, CheckCircle, Loader2
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [chatMessages, setChatMessages] = useState([
    { 
      type: 'bot', 
      message: 'Hello! 👋 Welcome to Playmatez Support. How can I help you today?',
      options: [
        'I have a question about the app',
        'I want to report an issue',
        'I need help with my account',
        'Partnership inquiry',
        'Other'
      ]
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState('form');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChatOption = (option) => {
    setChatMessages(prev => [
      ...prev,
      { type: 'user', message: option },
      { 
        type: 'bot', 
        message: getResponseForOption(option),
        showForm: true
      }
    ]);
  };

  const getResponseForOption = (option) => {
    const responses = {
      'I have a question about the app': 'Great! Please describe your question in detail below, and our team will get back to you within 24 hours.',
      'I want to report an issue': 'We\'re sorry to hear you\'re experiencing issues. Please provide details about the problem, including any error messages you\'ve seen.',
      'I need help with my account': 'We\'re here to help with your account. Please describe your issue and include the email associated with your Playmatez account.',
      'Partnership inquiry': 'Interested in partnering with Playmatez? Tell us about your organization and how you\'d like to collaborate.',
      'Other': 'Please share your query below and we\'ll do our best to assist you.',
    };
    return responses[option] || 'Please share your query below.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create mailto link with form data
    const subject = encodeURIComponent(formData.subject || 'Contact Form Submission');
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    
    // Open email client
    window.location.href = `mailto:connect@playmatez.io?subject=${subject}&body=${body}`;
    
    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      content: '7-54/13/2, Balasaraswathi Nagar, Malkajgiri, Hyderabad, Telangana, 500047',
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: 'connect@playmatez.io',
      href: 'mailto:connect@playmatez.io',
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp',
      content: 'Message us on WhatsApp',
      href: 'https://wa.me/61466274575',
    },
    {
      icon: Clock,
      title: 'Response Time',
      content: 'We typically respond within 24 hours',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              Get In Touch
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              Let's <span className="gradient-text">Connect</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Have questions, feedback, or partnership inquiries? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.div
                  key={info.title}
                  className="p-6 bg-card rounded-2xl border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{info.title}</h3>
                  {info.href ? (
                    <a
                      href={info.href}
                      target={info.href.startsWith('http') ? '_blank' : undefined}
                      rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-muted-foreground text-sm hover:text-primary transition-colors"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-muted-foreground text-sm">{info.content}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form / Chatbot */}
            <motion.div
              className="bg-card rounded-2xl border border-white/10 overflow-hidden"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Tabs */}
              <div className="flex border-b border-white/10">
                <button
                  onClick={() => setActiveTab('form')}
                  className={`flex-1 px-6 py-4 font-medium transition-colors ${
                    activeTab === 'form' 
                      ? 'text-primary border-b-2 border-primary bg-primary/5' 
                      : 'text-muted-foreground hover:text-white'
                  }`}
                  data-testid="contact-form-tab"
                >
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Contact Form
                </button>
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`flex-1 px-6 py-4 font-medium transition-colors ${
                    activeTab === 'chat' 
                      ? 'text-primary border-b-2 border-primary bg-primary/5' 
                      : 'text-muted-foreground hover:text-white'
                  }`}
                  data-testid="chatbot-tab"
                >
                  <HelpCircle className="w-4 h-4 inline mr-2" />
                  Quick Help
                </button>
              </div>

              {activeTab === 'form' ? (
                <div className="p-8">
                  {isSubmitted ? (
                    <motion.div
                      className="text-center py-12"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white mb-2">
                        Message Sent!
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        Your email client should have opened. If not, please email us directly at connect@playmatez.io
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="text-primary hover:underline"
                      >
                        Send another message
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Your Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 bg-muted/50 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
                            placeholder="John Doe"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 bg-muted/50 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
                            placeholder="john@example.com"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-muted/50 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="How can we help?"
                          data-testid="contact-subject-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Message
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={5}
                          className="w-full px-4 py-3 bg-muted/50 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                          placeholder="Tell us more about your query..."
                          data-testid="contact-message-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-6 py-4 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <div className="p-6 h-[500px] flex flex-col">
                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                    {chatMessages.map((msg, index) => (
                      <div key={index}>
                        <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[80%] p-4 rounded-2xl ${
                              msg.type === 'user'
                                ? 'bg-primary text-white rounded-br-none'
                                : 'bg-muted text-white rounded-bl-none'
                            }`}
                          >
                            {msg.message}
                          </div>
                        </div>
                        {msg.options && (
                          <div className="mt-3 space-y-2">
                            {msg.options.map((option, i) => (
                              <button
                                key={i}
                                onClick={() => handleChatOption(option)}
                                className="block w-full text-left px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-sm text-muted-foreground hover:text-white transition-colors border border-white/10"
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                        {msg.showForm && (
                          <div className="mt-4 p-4 bg-white/5 rounded-xl">
                            <p className="text-sm text-muted-foreground mb-3">
                              Please fill out the contact form to submit your query:
                            </p>
                            <button
                              onClick={() => setActiveTab('form')}
                              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                            >
                              Go to Contact Form
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Map */}
            <motion.div
              className="bg-card rounded-2xl border border-white/10 overflow-hidden"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-6 border-b border-white/10">
                <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Our Location
                </h3>
                <p className="text-muted-foreground text-sm mt-1">
                  7-54/13/2, Balasaraswathi Nagar, Malkajgiri, Hyderabad, Telangana, 500047
                </p>
              </div>
              <div className="h-[400px] lg:h-[calc(100%-88px)]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.2893842651897!2d78.52!3d17.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDI3JzAwLjAiTiA3OMKwMzEnMTIuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Playmatez India Office Location"
                  data-testid="location-map"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
